import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Edit,
  Delete,
  Add,
  ArrowBack,
  Info,
  LocationOn,
} from "@mui/icons-material";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Chip,
  InputAdornment,
  Tooltip,
} from "@mui/material";

// Example lists for departments, roles, and shifts; adjust as needed
const departments = ["Development", "HR", "Marketing", "Sales"];
const roles = [
  "Software Developer",
  "Team Lead",
  "Project Manager",
  "HR Manager",
  "Sales Executive",
  "Digital Marketing Manager",
];
const shifts = [
  "No Shift",
  "Weekday (9:00 AM - 6:00 PM)",
  "Night Shift (8:00 PM - 5:00 AM)",
];

const EmployeePage = () => {
  // State for employee data and UI
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Our form data keys must match what the backend expects.
  // Now password is auto-filled when editing if available
  const [formData, setFormData] = useState({
    employee_name: "",
    email: "",
    password: "",
    mobile: "",
    role: "",
    manager: "",
    department: "",
    shift: "",
    company_name: "",
    location: "",
  });

  const employeesPerPage = 10;
  const totalPages = Math.ceil(employees.length / employeesPerPage);
  const displayedEmployees = employees.slice(
    (currentPage - 1) * employeesPerPage,
    currentPage * employeesPerPage
  );

  // Base URL for your employee API endpoints
  const BASE_API_URL = "https://attendanceapi.gensparc.com/employee";

  // Fetch all employees from backend API
  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${BASE_API_URL}/list`, config);
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Reset current page if necessary
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [employees, totalPages, currentPage]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSelectEmployee = useCallback((id) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((empId) => empId !== id) : [...prev, id]
    );
  }, []);

  // Delete employees by calling DELETE endpoint for each selected employee
  const handleDelete = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      for (const emp of employees.filter((e) =>
        selectedEmployees.includes(e.id)
      )) {
        await axios.delete(`${BASE_API_URL}/delete/${emp.id}`, config);
      }
      fetchEmployees();
      setSelectedEmployees([]);
    } catch (error) {
      console.error("Error deleting employees:", error);
    }
  }, [selectedEmployees, employees]);

  // When clicking Add or Edit, open the form with pre-filled values if editing
  const handleAddClick = (employee = null) => {
    if (employee) {
      setFormData({
        employee_name: employee.employee_name,
        email: employee.email,
        mobile: employee.mobile || "",
        // Auto-fill existing password so manager can see/change it
        password: employee.password || "",
        role: employee.role,
        manager: employee.manager,
        department: employee.department,
        shift: employee.shift,
        company_name: employee.company_name || "",
        location: employee.location,
      });
    } else {
      setFormData({
        employee_name: "",
        email: "",
        password: "",
        mobile: "",
        role: "",
        manager: "",
        department: "",
        shift: "",
        company_name: "",
        location: "",
      });
    }
    setValidationErrors({});
    setEditingEmployee(employee);
    setShowForm(true);
  };

  // Submit the form: if editing, call PUT /update/:id; otherwise, POST /add
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    const errors = {};

    if (!formData.employee_name.trim())
      errors.employee_name = "Name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!editingEmployee && !formData.password)
      errors.password = "Password is required for new employee";
    if (formData.mobile && formData.mobile.length !== 10)
      errors.mobile = "Mobile number must be 10 digits";

    // ✅ Validate employee_id when updating
    if (editingEmployee && !editingEmployee.employee_id) {
      errors.employee_id = "Employee ID is missing for update";
    }

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingEmployee) {
        const payload = {
          employee_id: editingEmployee.employee_id, // keep for validation
          employee_name: formData.employee_name,
          email: formData.email,
          mobile: formData.mobile || "",
          password: formData.password || "",
          manager: formData.manager,
          department: formData.department,
          role: formData.role,
          shift: formData.shift,
          company_name: formData.company_name || "",
          location: formData.location || "",
        };

        // ← Use editingEmployee.id here, not employee_id
        await axios.put(
          `${BASE_API_URL}/update/${editingEmployee.id}`,
          payload,
          config
        );
      } else {
        await axios.post(
          `${BASE_API_URL}/add`,
          {
            employee_name: formData.employee_name,
            employee_id: `Emp${String(employees.length + 1).padStart(3, "0")}`,
            email: formData.email,
            mobile: formData.mobile,
            password: formData.password,
            role: formData.role,
            manager: formData.manager,
            department: formData.department,
            shift: formData.shift,
            company_name: formData.company_name,
            location: formData.location,
          },
          config
        );
      }

      fetchEmployees();
      setShowForm(false);
      setEditingEmployee(null);
      setFormData({
        employee_name: "",
        email: "",
        password: "",
        mobile: "",
        role: "",
        manager: "",
        department: "",
        shift: "",
        company_name: "",
        location: "",
      });
    } catch (error) {
      console.error("Error submitting employee data:", error);
      console.error("Status:", error.response?.status);
      console.error("Response headers:", error.response?.headers);
      console.error("Response body:", error.response?.data);
      console.log(employees[0]);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (["employee_name", "email"].includes(e.target.name)) {
      setValidationErrors((prev) => ({ ...prev, [e.target.name]: false }));
    }
  };

  const handleCellClick = (content) => {
    setDialogContent(content);
    setOpenDialog(true);
  };

  const commonStyles = {
    fontWeight: "bold",
    fontSize: "12px",
    whiteSpace: "nowrap",
    padding: "8px",
    textAlign: "center",
  };

  // for info tooltip in table cells
  const renderTableCell = (content) => {
    if (!content) return "";

    const stringContent = content.toString();
    const isLong = stringContent.length > 20;

    return isLong ? (
      <Tooltip title={stringContent} arrow>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            cursor: "default",
          }}
        >
          {stringContent.substring(0, 20)}...
          <Info fontSize="small" style={{ marginLeft: 4, color: "#888" }} />
        </span>
      </Tooltip>
    ) : (
      stringContent
    );
  };

  return (
    <div style={{ marginRight: "0", paddingLeft: "5px", paddingRight: "5px" }}>
      <div className="container-fluid px-0 py-5">
        <Card sx={{ padding: 3, marginBottom: 3, boxShadow: 3 }}>
          <div className="d-flex justify-content-between align-items-center">
            <Typography
              variant="h4"
              component="h1"
              color="textPrimary"
              sx={{ fontWeight: 600 }}
            >
              Employee Management
            </Typography>
            <div>
              <Button
                onClick={() => handleAddClick()}
                variant="contained"
                color="primary"
                startIcon={<Add />}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                  marginRight: 1,
                }}
                aria-label="Add Employee"
              >
                Add
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                startIcon={<Delete />}
                disabled={selectedEmployees.length === 0}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                }}
                aria-label="Delete Selected Employees"
              >
                Delete
              </Button>
            </div>
          </div>
        </Card>

        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f4f5f7" }}>
              <TableRow>
                <TableCell sx={commonStyles}>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedEmployees(employees.map((emp) => emp.id));
                      } else {
                        setSelectedEmployees([]);
                      }
                    }}
                    checked={
                      selectedEmployees.length > 0 &&
                      selectedEmployees.length === employees.length
                    }
                  />
                </TableCell>
                {[
                  "Employee",
                  "Employee ID",
                  "Manager",
                  "Department",
                  "Role",
                  "Shift",
                  "Work Location",
                  "Last Known Location",
                  "Action",
                ].map((heading, index) => (
                  <TableCell key={index} sx={commonStyles}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={commonStyles}>
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                displayedEmployees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    sx={{ "&:hover": { backgroundColor: "#e8f5fe" } }}
                  >
                    <TableCell align="center" sx={{ padding: "8px" }}>
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(employee.id)}
                        onChange={() => handleSelectEmployee(employee.id)}
                      />
                    </TableCell>
                    <TableCell sx={commonStyles}>
                      {renderTableCell(employee.employee_name)}
                    </TableCell>
                    <TableCell sx={commonStyles}>
                      {renderTableCell(employee.employee_id)}
                    </TableCell>
                    <TableCell sx={commonStyles}>
                      {renderTableCell(employee.manager)}
                    </TableCell>
                    <TableCell sx={commonStyles}>
                      {renderTableCell(employee.department)}
                    </TableCell>
                    <TableCell sx={commonStyles}>
                      {renderTableCell(employee.role)}
                    </TableCell>
                    <TableCell sx={commonStyles}>
                      {renderTableCell(employee.shift)}
                    </TableCell>
                    <TableCell sx={commonStyles}>
                      {renderTableCell(employee.company_name)}
                    </TableCell>
                    <TableCell sx={commonStyles}>
                      {renderTableCell(employee.location)}
                    </TableCell>
                    <TableCell sx={commonStyles}>
                      <div className="d-flex justify-content-center align-items-center">
                        <IconButton
                          sx={{
                            backgroundColor: "#ffeb3b",
                            borderRadius: "10px",
                            "&:hover": { backgroundColor: "#fbc02d" },
                          }}
                          aria-label="Edit Employee"
                          onClick={() => handleAddClick(employee)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        {/* <IconButton
                          sx={{
                            backgroundColor: "#00bcd4",
                            borderRadius: "10px",
                            "&:hover": { backgroundColor: "#008c9e" },
                            marginLeft: 1,
                          }}
                          aria-label="Locate Employee"
                        >
                          <LocationOn fontSize="small" />
                        </IconButton> */}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="d-flex justify-content-between align-items-center mt-4">
          <Typography
            variant="body1"
            sx={{ fontSize: "14px", fontWeight: 500 }}
          >
            Page {currentPage} of {totalPages}
          </Typography>
          <div>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              sx={{
                marginRight: 1,
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
              aria-label="Previous Page"
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              sx={{
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
              aria-label="Next Page"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Dialog for Full Content */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Full Content</DialogTitle>
        <DialogContent>
          <p>{dialogContent}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Employee Dialog */}
      <Dialog
        open={showForm}
        onClose={() => setShowForm(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => setShowForm(false)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">
            {editingEmployee ? "Edit Employee" : "Add New Employee"}
          </Typography>
        </DialogTitle>

        <Box component="form" onSubmit={handleFormSubmit} sx={{ p: 3 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "20px",
            }}
          >
            {/* Left Column */}
            <div>
              <TextField
                fullWidth
                required
                label="Employee Name"
                name="employee_name"
                value={formData.employee_name}
                onChange={handleInputChange}
                error={Boolean(validationErrors.employee_name)}
                helperText={validationErrors.employee_name}
                margin="normal"
              />
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                error={Boolean(validationErrors.email)}
                helperText={validationErrors.email}
                margin="normal"
              />
              <TextField
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                fullWidth
                type={showPassword ? "text" : "password"}
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                error={Boolean(validationErrors.password)}
                helperText={validationErrors.password}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Mobile No."
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                error={Boolean(validationErrors.mobile)}
                helperText={
                  validationErrors.mobile
                    ? validationErrors.mobile
                    : "10 digits required"
                }
                margin="normal"
                inputProps={{ maxLength: 10 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">+91</InputAdornment>
                  ),
                }}
              />
              {/* Employee ID field removed to force auto-generation */}
            </div>

            {/* Right Column */}
            <div>
              <TextField
                fullWidth
                label="Manager"
                name="manager"
                value={formData.manager}
                onChange={handleInputChange}
                margin="normal"
                placeholder="Search by name or mobile"
              />

              <TextField
                fullWidth
                select
                required
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                margin="normal"
              >
                <MenuItem value="">
                  <em>Select Department</em>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                select
                required
                label="Role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                margin="normal"
              >
                <MenuItem value="">
                  <em>Select Role</em>
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <TextField
              fullWidth
              required
              select
              label="Shift"
              name="shift"
              value={formData.shift}
              onChange={handleInputChange}
              margin="normal"
            >
              {shifts.map((shift) => (
                <MenuItem key={shift} value={shift}>
                  {shift}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Work Location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <Chip
                    label={`${formData.location ? 1 : 0} locations selected`}
                    onDelete={() => setFormData({ ...formData, location: "" })}
                  />
                ),
              }}
            />
          </div>

          <DialogActions sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setShowForm(false)}
              sx={{ borderRadius: "20px" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ borderRadius: "20px" }}
            >
              {editingEmployee ? "Update" : "Submit"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

export default EmployeePage;
